import type { NumericValue } from '@/lib/crm/list/utils/aggregationTransformers';
import { formatCurrencyValue } from '@/lib/crm/list/utils/aggregationTransformers';

/**
 * Reserved chart-datapoint key carrying the per-measure currency breakdown so
 * the tooltip can render it. Registered in NON_MEASURE_ROW_KEYS so it is never
 * treated as a measure/series.
 */
export const CURRENCY_BREAKDOWN_KEY = '__currencyBreakdowns';

// ISO 4217 defines roughly 180 active currency codes, so this generously
// bounds any realistic per-cell breakdown while capping worst-case parsing
// work against a malformed/unbounded backend array.
const MAX_CURRENCY_ENTRIES = 256;

/**
 * Detect the per-currency aggregate shape the native backend emits when
 * convert_to is None (Issue #13623): a list of { amount, unit } subtotals.
 * Returns the parsed subtotals (finite amounts only) when `value` is an array,
 * or null for any non-array (scalar/converted) value — leaving the scalar path
 * byte-identical. An empty array is a valid (empty) aggregate → returns [].
 */
export function parseCurrencyAggregate(value: unknown): NumericValue[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const totals: NumericValue[] = [];
  // Bound iteration by the RAW entry index, not by the count of parsed totals:
  // a large array of malformed/skipped entries never grows `totals`, so a
  // parsed-count guard would still scan every element. Capping the scan window
  // keeps worst-case work bounded regardless of how many entries are valid.
  const scanLimit = Math.min(value.length, MAX_CURRENCY_ENTRIES);
  for (let i = 0; i < scanLimit; i++) {
    const entry = value[i];
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const { amount, unit } = entry as { amount?: unknown; unit?: unknown };
    // Only coerce numbers and non-empty numeric strings. `Number('')`,
    // `Number(false)`, and `Number(null)` all yield a finite 0, which would
    // fabricate a zero subtotal for a missing/non-numeric amount.
    let numeric: number;
    if (typeof amount === 'number') {
      numeric = amount;
    } else if (typeof amount === 'string' && amount.trim() !== '') {
      numeric = Number(amount);
    } else {
      continue;
    }
    if (!Number.isFinite(numeric)) {
      continue;
    }
    totals.push({
      amount: numeric,
      unit: typeof unit === 'string' && unit ? unit : null,
    });
  }
  if (value.length > MAX_CURRENCY_ENTRIES) {
    console.warn(
      `[parseCurrencyAggregate] truncated ${value.length} currency entries to ${MAX_CURRENCY_ENTRIES}`
    );
  }
  return totals;
}

/**
 * The single plottable amount for a per-currency aggregate, or null when it
 * cannot share one numeric axis: multi-unit (two currencies can't share an
 * axis) or empty. Single-unit → that unit's amount.
 */
export function singleUnitChartValue(totals: NumericValue[]): number | null {
  return totals.length === 1 ? totals[0].amount : null;
}

/**
 * Render the per-currency breakdown for a hovered measure from a chart
 * datapoint's payload, or null when the datapoint carries no breakdown for
 * that measure (scalar/pie payloads never stamp one).
 */
export function currencyBreakdownTooltip(
  payload: unknown,
  measureKey: string
): string | null {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }
  const breakdowns = (payload as Record<string, unknown>)[
    CURRENCY_BREAKDOWN_KEY
  ];
  if (typeof breakdowns !== 'object' || breakdowns === null) {
    return null;
  }
  const list = (breakdowns as Record<string, unknown>)[measureKey];
  if (!Array.isArray(list)) {
    return null;
  }
  return formatCurrencyValue(list as NumericValue[]);
}
